import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type ColumnFiltersState,
  type ExpandedState,
  type Row,
  type RowData,
  type RowSelectionState,
  type SortingState,
  type VisibilityState,
} from "@tanstack/react-table";
import { ArrowUpDown } from "lucide-react";
import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { cn } from "@/shared/lib/cn";
import { Button } from "@/shared/ui/button/Button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/ui/table/Table";

import { isDataColumn } from "./columnUtils";
import { ColumnFilterControl } from "./internal/ColumnFilterControl";
import { DataTableToolbar } from "./internal/DataTableToolbar";

/**
 * Per-column extras our data grids understand. `label` gives the
 * column-visibility menu and CSV export a human name (falling back to a
 * string header, then the column id); `align` right-aligns numeric columns.
 */
declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    label?: string;
    align?: "left" | "center" | "right";
    /** Header-filter control: a text box (default) or a dropdown of `filterOptions`. */
    filterVariant?: "text" | "select";
    /** Options for a `select` header filter. */
    filterOptions?: { label: string; value: string }[];
    /** Fixed column width (any CSS width) — applied only under `fixedLayout`. Lets an
     * expanded sub-row line its own columns up with the parent's. */
    width?: string;
  }
}

/**
 * Reusable headless data grid (TanStack Table v8 + shared/ui primitives).
 * The single table pattern every list screen uses (PLX §12). Server-side
 * pagination is driven by the parent (RTK Query); this renders one page.
 *
 * Advanced operations (column show/hide, CSV export, density, client search)
 * are opt-in props so server-paged screens stay correct — client search and
 * sort only ever see the current page, so leave them off where the parent
 * already searches/paginates server-side.
 */
export interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  isLoading?: boolean;
  emptyMessage?: string;
  /** Client-side sort on the current page (server sort is passed via query). */
  enableSorting?: boolean;
  onRowClick?: (row: TData) => void;
  className?: string;
  /**
   * Enable TanStack row selection (checkbox columns drive it via
   * `row.getIsSelected()` / `table.getIsAllPageRowsSelected()`). Off by
   * default so existing list screens are unaffected.
   */
  enableRowSelection?: boolean;
  /**
   * Per-row predicate deciding whether a row can reveal a detail panel.
   * When it returns true and the row is toggled expanded, `renderSubRow`
   * is rendered in a full-width row directly beneath it.
   */
  getRowCanExpand?: (row: Row<TData>) => boolean;
  /** Detail panel rendered under an expanded row (spans every column). */
  renderSubRow?: (row: TData) => React.ReactNode;
  /**
   * Fix column widths from each column's `meta.width` (CSS `table-layout: fixed`) and
   * drop the sub-row's inset padding, so an expanded panel can align its own columns
   * with the header. Off by default — auto-sized columns are unchanged.
   */
  fixedLayout?: boolean;

  /* ── Toolbar (advanced operations) ── */
  /**
   * Left-aligned slot in the toolbar — house the screen's existing search
   * box, entity filters, or bulk-action buttons here so everything reads as
   * one control strip. Rendered only when at least one toolbar feature or
   * this slot is present.
   */
  toolbar?: ReactNode;
  /** Show a column show/hide menu on the right of the toolbar. */
  enableColumnVisibility?: boolean;
  /**
   * Show a per-column filter control on each data-column header (a text box,
   * or a dropdown for columns whose `meta.filterVariant` is "select").
   * Filtering is client-side over the rows currently rendered — on server-paged
   * screens that is the loaded page, so pair it with the server search/filters.
   */
  enableColumnFilters?: boolean;
  /** Show a comfortable/compact density toggle. */
  enableDensityToggle?: boolean;
  /** Start the table in the compact (dense) row height — for long, scan-heavy lists. */
  initialDense?: boolean;
  /** Paginate client-side at this page size (all rows already loaded). Omit for a single page. */
  pageSize?: number;
  /**
   * Show an "Export CSV" button that downloads the currently-rendered rows
   * (respecting sort, filter and column visibility). Value is the file's
   * base name, e.g. `"employers"` → `employers.csv`.
   */
  exportFileName?: string;
  /**
   * Client-side global search over the rendered rows. ONLY use this for
   * tables that already hold every row — server-paged screens must search
   * server-side (pass their own input into `toolbar` instead).
   */
  enableGlobalFilter?: boolean;
  searchPlaceholder?: string;
  /**
   * Sort the table starts on. The user can still re-sort from the headers —
   * this only replaces "whatever order the server sent" as the opening view.
   */
  initialSorting?: SortingState;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  isLoading = false,
  emptyMessage = "No results.",
  enableSorting = true,
  onRowClick,
  className,
  enableRowSelection = false,
  getRowCanExpand,
  renderSubRow,
  fixedLayout = false,
  toolbar,
  enableColumnVisibility = false,
  enableColumnFilters = false,
  enableDensityToggle = false,
  initialDense = false,
  pageSize,
  exportFileName,
  enableGlobalFilter = false,
  searchPlaceholder = "Search…",
  initialSorting,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = useState<SortingState>(initialSorting ?? []);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [dense, setDense] = useState(initialDense);

  // An expanded sub-row lives inside the horizontally-scrolling table, so its content
  // would otherwise stretch to the full (scrolled) table width and push its own right-hand
  // content off-screen. Measure the visible viewport and pin the sub-row to it (sticky) so
  // the expansion always reads within view, whatever the table's horizontal scroll.
  const scrollRef = useRef<HTMLDivElement>(null);
  const [subRowWidth, setSubRowWidth] = useState<number>();
  const hasSubRow = Boolean(renderSubRow);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !hasSubRow) return;
    const measure = () => setSubRowWidth(el.clientWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasSubRow]);

  const table = useReactTable({
    data,
    columns,
    state: { sorting, rowSelection, expanded, columnVisibility, columnFilters, globalFilter },
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    onExpandedChange: setExpanded,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    enableColumnFilters,
    enableRowSelection,
    getRowCanExpand,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: enableSorting ? getSortedRowModel() : undefined,
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getRowCanExpand ? getExpandedRowModel() : undefined,
    getPaginationRowModel: pageSize ? getPaginationRowModel() : undefined,
    initialState: pageSize ? { pagination: { pageSize } } : undefined,
  });

  const hideableColumns = useMemo(
    () => table.getAllColumns().filter((c) => c.getCanHide() && isDataColumn(c)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [table, columnVisibility]
  );

  const showToolbar =
    Boolean(toolbar) ||
    enableColumnVisibility ||
    enableDensityToggle ||
    Boolean(exportFileName) ||
    enableGlobalFilter;

  return (
    <div className={cn("space-y-3", className)}>
      {showToolbar ? (
        <DataTableToolbar
          table={table}
          toolbar={toolbar}
          enableGlobalFilter={enableGlobalFilter}
          globalFilter={globalFilter}
          onGlobalFilterChange={setGlobalFilter}
          searchPlaceholder={searchPlaceholder}
          enableDensityToggle={enableDensityToggle}
          dense={dense}
          onToggleDense={() => setDense((d) => !d)}
          exportFileName={exportFileName}
          enableColumnVisibility={enableColumnVisibility}
          hideableColumns={hideableColumns}
        />
      ) : null}

      <div
        ref={scrollRef}
        className="overflow-x-auto rounded-lg border border-border bg-card shadow-sm"
      >
        <Table
          className={cn(
            dense && "[&_td]:py-1.5 [&_th]:py-2",
            // Fixed layout needs a floor width so money columns never crush; the wrapper
            // scrolls it horizontally on narrower panes, sub-row included, so both stay aligned.
            fixedLayout && "w-full min-w-[78rem] table-fixed"
          )}
        >
          <TableHeader>
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id}>
                {hg.headers.map((header) => {
                  const canSort = enableSorting && header.column.getCanSort();
                  const align = header.column.columnDef.meta?.align;
                  const canFilter =
                    enableColumnFilters &&
                    header.column.getCanFilter() &&
                    isDataColumn(header.column);
                  return (
                    <TableHead
                      key={header.id}
                      style={
                        fixedLayout ? { width: header.column.columnDef.meta?.width } : undefined
                      }
                      className={cn(
                        align === "right" && "text-right",
                        align === "center" && "text-center"
                      )}
                    >
                      {header.isPlaceholder ? null : (
                        <div
                          className={cn(
                            "flex items-center gap-1",
                            align === "right" && "justify-end",
                            align === "center" && "justify-center"
                          )}
                        >
                          {canSort ? (
                            <Button
                              type="button"
                              variant="ghost"
                              className="inline-flex h-auto items-center gap-1 bg-transparent p-0 font-medium hover:bg-transparent hover:text-foreground"
                              onClick={header.column.getToggleSortingHandler()}
                            >
                              {flexRender(header.column.columnDef.header, header.getContext())}
                              <ArrowUpDown className="h-3.5 w-3.5 opacity-60" aria-hidden />
                            </Button>
                          ) : (
                            flexRender(header.column.columnDef.header, header.getContext())
                          )}
                          {canFilter ? <ColumnFilterControl column={header.column} /> : null}
                        </div>
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center text-muted-foreground"
                >
                  Loading…
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row) => (
                <Fragment key={row.id}>
                  <TableRow
                    className={cn(onRowClick && "cursor-pointer")}
                    data-state={row.getIsSelected() ? "selected" : undefined}
                    onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                  >
                    {row.getVisibleCells().map((cell) => {
                      const align = cell.column.columnDef.meta?.align;
                      return (
                        <TableCell
                          key={cell.id}
                          style={
                            fixedLayout ? { width: cell.column.columnDef.meta?.width } : undefined
                          }
                          className={cn(
                            align === "right" && "text-right",
                            align === "center" && "text-center"
                          )}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                  {renderSubRow && row.getIsExpanded() ? (
                    <TableRow className="bg-muted/30 hover:bg-muted/30">
                      <TableCell colSpan={row.getVisibleCells().length} className="p-0">
                        {/* Fixed layout: the panel spans the whole (scrollable) table so its own
                            columns line up with the header. Otherwise it's pinned to the viewport
                            so a wide auto-table's expansion still reads within view. */}
                        <div
                          className={cn(fixedLayout ? "w-full" : "sticky left-0 p-6")}
                          style={!fixedLayout && subRowWidth ? { width: subRowWidth } : undefined}
                        >
                          {renderSubRow(row.original)}
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : null}
                </Fragment>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center text-muted-foreground"
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {pageSize && table.getPageCount() > 1 ? (
        <div className="flex items-center justify-between px-1 text-sm text-muted-foreground">
          <span>
            Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
